import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../cn';
import { CloseIcon } from '../icons';
import { Button } from './button';

export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;

export interface DrawerContentProps {
  title: ReactNode;
  /** Small line above the title, e.g. a reference id. */
  eyebrow?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** 560px default; 720px for wide editors. */
  width?: 'default' | 'wide';
  /** Actions next to the close button (e.g. "Open full record"). */
  headerActions?: ReactNode;
  className?: string;
  modal?: boolean;
}

/**
 * Side panel that opens from the inline end (right in English, left in Arabic).
 * Used for editing part of a record or previewing a record while keeping the list visible.
 */
export function DrawerContent({ title, eyebrow, description, children, footer, width = 'default', headerActions, className }: DrawerContentProps) {
  const { t } = useTranslation();
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-(--z-drawer) bg-scrim data-[state=open]:animate-fade-in" />
      <DialogPrimitive.Content
        className={cn(
          'fixed inset-y-0 end-0 z-(--z-drawer) flex w-full flex-col border-s border-border bg-surface shadow-overlay outline-none data-[state=open]:animate-drawer-in',
          width === 'wide' ? 'max-w-drawer-wide' : 'max-w-drawer',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            {eyebrow ? <div className="text-meta text-ink-muted">{eyebrow}</div> : null}
            <DialogPrimitive.Title className="truncate text-h2 text-ink">{title}</DialogPrimitive.Title>
            {description ? <DialogPrimitive.Description className="text-body text-ink-secondary">{description}</DialogPrimitive.Description> : <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {headerActions}
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="sm" className="w-7 px-0" aria-label={t('actions.close')}>
                <CloseIcon />
              </Button>
            </DialogPrimitive.Close>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
        {footer ? <div className="flex items-center justify-end gap-2 border-t border-border bg-surface px-5 py-3">{footer}</div> : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
